"use client"

import { FragmentType, graphql, useFragment } from "@/gql"
import { Text, Image } from "@/elements"
import {
  JobTitleColorised,
  RedactedClientAvatarWithNameAndTitle,
  RedactedCreativeAvatarWithNameAndTitle,
} from "@/components"
import { RedactedCompactJobsFeedItemFragmentDoc } from "@/gql/graphql"
import { formatDateDiffDisplay } from "@/utils/formatDateDisplay"

graphql(`
  fragment RedactedCompactJobsFeedItem on JobPosting {
    id

    coverImage {
      id
      thumbnailUrl645: thumbnailUrl(width: 645)
    }
    publishedAt
    owner {
      id
      __typename

      ...RedactedClientAvatarWithNameAndTitle
      ...RedactedCreativeAvatarWithNameAndTitle
    }

    ...JobTitleColorised
  }
`)

interface Props {
  jobPost: FragmentType<typeof RedactedCompactJobsFeedItemFragmentDoc> | null | undefined
}

export const RedactedCompactJobsFeedItem: React.FC<Props> = props => {
  const jobPost = useFragment(RedactedCompactJobsFeedItemFragmentDoc, props.jobPost)

  const ownerTypename = jobPost?.owner?.__typename
  const owner =
    ownerTypename &&
    ["IndividualClient", "IndividualCreative", "RedactedIndividualClient", "RedactedIndividualCreative"].includes(
      ownerTypename
    )
      ? jobPost?.owner
      : null
  const ownerIsRedactedClient = owner?.__typename === "RedactedIndividualClient"
  const ownerIsRedactedCreative = owner?.__typename === "RedactedIndividualCreative"

  const image = jobPost?.coverImage?.thumbnailUrl645

  const publishedAtFormatted = jobPost?.publishedAt ? formatDateDiffDisplay(jobPost.publishedAt, true) : ""

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col">
        {ownerIsRedactedClient ? (
          <RedactedClientAvatarWithNameAndTitle
            size="sm"
            variant="sm"
            client={owner}
            canClick={false}
            subtitleMaxLines={1}
          />
        ) : null}
        {ownerIsRedactedCreative ? (
          <RedactedCreativeAvatarWithNameAndTitle
            size="sm"
            variant="sm"
            creative={owner}
            canClick={false}
            subtitleMaxLines={1}
          />
        ) : null}
      </div>

      <div className="flex w-full flex-row items-end justify-between">
        <div className="flex w-full flex-row">
          <div className="flex w-full flex-col pr-5">
            <JobTitleColorised
              jobPost={jobPost}
              textVariant="body1"
              commonWordColor="text-fg-2"
              focusWordColor="text-fg-1"
            />
          </div>
          {image && (
            <div className="relative flex size-[100px] shrink-0 overflow-hidden rounded-md bg-bg-3">
              <Image
                srcSetUrls={{
                  sm: image,
                }}
                alt="Cover image"
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>
        <div />
      </div>

      <div className="flex flex-row items-center justify-between">
        <Text variant="caption" className="text-fg-2">
          {publishedAtFormatted}
        </Text>
      </div>
    </div>
  )
}
